import { useEffect, useState } from "react";
import {
  RadioGroup,
  FormControl,
  FormControlLabel,
  FormHelperText,
} from "@mui/material";
import TextFieldCustom from "./elements/textfieldCustom";
import DatePickerCustom from "./elements/datePicker";
import RadioCustom from "./elements/radioCustom";
import CustomButton from "./elements/CustomButton/CustomButton";
import { gender } from "../constants/formConstants";
import axiosInstance from "../configuration/request";
import { useSnackbar } from "./SnackbarContext";
import { useSelector } from "react-redux";
import dayjs from "dayjs";
import { TbEdit } from "react-icons/tb";
import { formatDateToDDMMYYYY } from "../utils/dateFormat";
import { MdClose } from "react-icons/md";
import ProfileImageSection from "./profileImageSection";
import PhoneInput from "react-phone-input-2";
import "react-phone-input-2/lib/style.css";
import { profileRules } from "../utils/formValidation/validationRules";
import { validateForm } from "../utils/formValidation/validation";

export default function ProfileComponent({ BCrumb }) {
  const [formData, setFormData] = useState({
    name: "",
    email: "",
    mobile: "",
    dob: null,
    gender: "",
    profileImage: null,
  });
  const [isEditing, setIsEditing] = useState(false);
  const showSnackbar = useSnackbar();
  const { loginDetails } = useSelector((state) => state.counter);
  const userId = loginDetails.userId || null;
  const [userData, setUserData] = useState({});
  const [formErrors, setFormErrors] = useState({});

  useEffect(() => {
    fetchUser();
  }, []);

  const fetchUser = () => {
    axiosInstance
      .get(`/customer/users?id=${userId}`)
      .then((res) => {
        let data = res?.data?.data[0];
        const formattedUser = {
          name: data?.name || "",
          email: data?.email || "",
          mobile: data?.phone_number || "",
          dob: data?.dob ? dayjs(data?.dob) : null,
          gender: data?.gender || "",
          profileImage: data?.profile_img_url || "",
        };
        setFormData(formattedUser);
        setUserData(formattedUser);
      })
      .catch((err) => showSnackbar("Something went wrong!", "error"));
  };

  const handleSaveChanges = () => {
    // Validate form before creating payload
    const errors = validateForm(formData, profileRules);
    setFormErrors(errors);

    if (Object.keys(errors).length === 0) {
      const payload = {
        name: formData?.name || "",
        email: formData?.email || "",
        phone_number: formData?.mobile || "",
        dob: formData?.dob ? formatDateToDDMMYYYY(formData?.dob) : null,
        gender: formData?.gender || "",
        profile_img_url: formData.profileImage || null,
      };
      updateUser(payload);
    } else {
        showSnackbar("Please fix the errors in the form", "error");
    }
  };

  const SaveProfileImage = (imageUrl) => {
    const payload = {
      profile_img_url: imageUrl,
    };
    updateUser(payload);
  };

  const updateUser = (payload) => {
    axiosInstance
      .patch(`/customer/users/${userId}`, payload)
      .then((res) => {
        showSnackbar("Your details updated successfully");
        setIsEditing(false);
        fetchUser();
      })
      .catch((error) => {
        if (error?.response?.data?.message) {
          showSnackbar(error?.response?.data?.message, "error");
        } else {
          showSnackbar("Something went wrong!", "error");
        }
        fetchUser();
      });
  };

  // FIXED: Handle both standard events (name, email) and direct values (mobile)
  const handleChange = (field) => (e) => {
    const value = e?.target ? e.target.value : e; // Extract value from event or use direct value
    setFormData((prev) => ({ ...prev, [field]: value }));
    setFormErrors((prev) => ({ ...prev, [field]: "" }));
  };

  const handleDateChange = (field) => (newValue) => {
    setFormData((prev) => ({ ...prev, [field]: newValue }));
    setFormErrors((prev) => ({ ...prev, [field]: "" }));
  };

  return (
    <div>
      <h2 className="text-2xl font-[600] mb-3">
        Profile <span className="text-[#008ECC]">Information</span>
      </h2>

      <div className="bg-[var(--primary-bg-color)] p-6 rounded-xl shadow-[0_0_10px_rgba(0,0,0,0.06)]">
        <div className="flex flex-col md:flex-row justify-between items-center mb-6 gap-6 ">
          <div className="flex items-center gap-4">
            <ProfileImageSection
              imageUrl={userData?.profileImage}
              SaveProfileImage={SaveProfileImage}
            />
            <div>
              <h2 className="text-xl font-semibold capitalize text-[var(--primary-text-color)]">
                {userData?.name}
              </h2>
              <p className="text-gray-500 text-sm">{userData?.email}</p>
              <p className="text-gray-500 text-sm capitalize">
                {userData?.gender}
              </p>
            </div>
          </div>

          <CustomButton
            onClick={() => {
              if (isEditing) {
                setFormData(userData);
                setFormErrors({});
              }
              setIsEditing(!isEditing);
            }}
            customeClass="customer-light-button w-[80px] h-[35px] text-sm"
          >
            <span className="mr-1 text-[16px]">
              {isEditing ? <MdClose /> : <TbEdit />}
            </span>
            <span className="text-sm">{isEditing ? "Cancel" : "Edit"}</span>
          </CustomButton>
        </div>

        <div className="grid md:grid-cols-2 gap-y-0 gap-x-8 mb-2">
          <div>
            <div className="text-sm font-medium mb-1 text-gray-600">Full Name</div>
            <TextFieldCustom
              placeholder="Fullname"
              fullWidth
              value={formData.name}
              onChange={handleChange("name")}
              disabled={!isEditing}
              error={!!formErrors.name}
              helperText={formErrors.name || " "}
            />
          </div>

          <div>
            <div className="text-sm font-medium mb-1 text-gray-600">Mobile Number</div>
            <div>
              <PhoneInput
                country={"us"}
                value={formData.mobile}
                onChange={(val) => handleChange("mobile")(val)}
                inputClass="!w-full !h-[53px] !border !border-[#d1d5db] !rounded-[10px]"
                containerClass="w-full"
                disabled={!isEditing}
              />
              {formErrors.mobile && (
                <div className="text-red-600 text-xs mt-1">
                  {formErrors.mobile}
                </div>
              )}
            </div>
          </div>

          <div>
            <div className="text-sm font-medium mb-1 text-gray-600">Email</div>
            <TextFieldCustom
              placeholder="Email"
              fullWidth
              value={formData.email}
              onChange={handleChange("email")}
              disabled={true}
              error={!!formErrors.email}
              helperText={formErrors.email || " "}
            />
          </div>

          <div>
            <div className="text-sm font-medium mb-1 text-gray-600">Date of Birth</div>
            <DatePickerCustom
              value={formData.dob}
              onChange={handleDateChange("dob")}
              disabled={!isEditing}
              maxDate={dayjs()}
              error={!!formErrors.dob}
              helperText={formErrors.dob || " "}
            />
          </div>
        </div>

        <FormControl component="fieldset" className="w-full">
          <div className="text-sm font-medium text-gray-600">Gender</div>
          <RadioGroup
            value={formData.gender}
            onChange={handleChange("gender")}
            row
            disabled={!isEditing}
          >
            {gender.map((option) => (
              <FormControlLabel
                key={option}
                value={option}
                className="custom-radio-label"
                control={<RadioCustom disabled={!isEditing} />}
                label={option.charAt(0).toUpperCase() + option.slice(1).toLowerCase()}
              />
            ))}
          </RadioGroup>
          <FormHelperText error={!!formErrors.gender}>
            {formErrors.gender || " "}
          </FormHelperText>
        </FormControl>

        <div className="flex justify-end">
          <CustomButton
            onClick={handleSaveChanges}
            disabled={!isEditing}
            customeClass={`customer-dark-button w-[150px] h-[40px] ${
              !isEditing ? "opacity-60 cursor-not-allowed" : ""
            }`}
          >
            Save Changes
          </CustomButton>
        </div>
      </div>
    </div>
  );
}
